import {
  ThemePreference,
  UserPreferenceKey,
  type UserPreference,
  type UserPreferencesResponse,
} from '@kirin/api-client/contracts/user-preferences';

/**
 * Local-only mapping from `UserPreferenceKey` to the corresponding value
 * shape. `UserPreference` is a discriminated union over `key`, so
 * `Extract<UserPreference, { key: K }>['value']` resolves to the specific
 * value type for that key. Kept in this file because `@kirin/api-client`
 * does not export it (yet).
 */
type UserPreferenceValueByKey = {
  [K in UserPreferenceKey]: Extract<UserPreference, { key: K }>['value'];
};

/**
 * Type-safe selector for a single preference value out of the boot query
 * response. Returns `undefined` when the response is missing, the row is
 * missing, or the key is unknown.
 */
export function selectPreference<K extends UserPreferenceKey>(
  data: UserPreferencesResponse | undefined,
  key: K
): UserPreferenceValueByKey[K] | undefined {
  if (!data) {
    return undefined;
  }
  const match = data.preferences.find(p => p.key === key);
  if (!match) {
    return undefined;
  }
  return match.value as UserPreferenceValueByKey[K];
}

/**
 * Convenience helper that extracts the theme preference value.
 * Returns `undefined` when the response is missing or the theme row is absent.
 */
export function getThemeFromPreferences(
  data: UserPreferencesResponse | undefined
): ThemePreference | undefined {
  return selectPreference(data, UserPreferenceKey.Theme);
}
